/* eslint-disable prettier/prettier */
import appSettings from "appsettings.json";

export const extractCategoryName = (item) => {
  if (typeof item === "string") return item.trim();
  if (!item || typeof item !== "object") return "";

  return String(item.name || item.categoryName || item.title || item.category || "").trim();
};

export const extractCategoryImageUrl = (item) => {
  if (!item || typeof item !== "object") return "";

  const imageUrl = String(
    item.imageUrl || item.ImageUrl || item.image || item.thumbnail || item.thumbnailUrl || ""
  ).trim();

  if (!imageUrl) return "";
  if (
    imageUrl.startsWith("http://") ||
    imageUrl.startsWith("https://") ||
    imageUrl.startsWith("data:image/")
  ) {
    return imageUrl;
  }
  if (imageUrl.startsWith("/")) {
    return `${appSettings.baseUrl}${imageUrl}`;
  }
  return imageUrl;
};

export const extractCategory = (item) => {
  const name = extractCategoryName(item);
  if (!name) return null;

  const id = Number(item?.id ?? item?.Id ?? 0);
  const displayInCatRaw = item?.displayInCat ?? item?.DisplayInCat;
  const displayInMainRaw = item?.displayInMain ?? item?.DisplayInMain;
  const displayInSubRaw = item?.displayInSub ?? item?.DisplayInSub;
  const toBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1";
    }
    return false;
  };

  return {
    id: Number.isFinite(id) ? id : 0,
    name,
    imageUrl: extractCategoryImageUrl(item),
    displayInCat: toBool(displayInCatRaw),
    displayInMain: toBool(displayInMainRaw),
    displayInSub: toBool(displayInSubRaw),
  };
};

export const fetchPublicCategoryNames = async () => {
  const categoryEndpoint =
    appSettings.endpoints.getPublicCategories ||
    appSettings.endpoints.publicCategories ||
    appSettings.endpoints.getAllCategories ||
    appSettings.endpoints.getCategories ||
    appSettings.endpoints.categories;

  if (!categoryEndpoint) return [];

  const response = await fetch(`${appSettings.baseUrl}${categoryEndpoint}`);
  if (!response.ok) return [];

  const payload = await response.json();
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return items
    .map(extractCategory)
    .filter((item) => item && item.displayInCat)
    .map((item) => item.name)
    .filter(Boolean)
    .filter((name, index, arr) => arr.indexOf(name) === index);
};

export const fetchPublicCategories = async () => {
  const categoryEndpoint =
    appSettings.endpoints.getPublicCategories ||
    appSettings.endpoints.publicCategories ||
    appSettings.endpoints.getAllCategories ||
    appSettings.endpoints.getCategories ||
    appSettings.endpoints.categories;

  if (!categoryEndpoint) return [];

  const response = await fetch(`${appSettings.baseUrl}${categoryEndpoint}`);
  if (!response.ok) return [];

  const payload = await response.json();
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  const mapped = items.map(extractCategory).filter((item) => item && item.displayInCat);
  const dedupedByName = [];
  const seen = new Set();

  mapped.forEach((item) => {
    const key = item.name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    dedupedByName.push(item);
  });

  return dedupedByName;
};

export const fetchAllPublicCategories = async () => {
  const categoryEndpoint =
    appSettings.endpoints.getPublicCategories ||
    appSettings.endpoints.publicCategories ||
    appSettings.endpoints.getAllCategories ||
    appSettings.endpoints.getCategories ||
    appSettings.endpoints.categories;

  if (!categoryEndpoint) return [];

  const response = await fetch(`${appSettings.baseUrl}${categoryEndpoint}`);
  if (!response.ok) return [];

  const payload = await response.json();
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  const mapped = items.map(extractCategory).filter(Boolean);
  const dedupedByName = [];
  const seen = new Set();

  mapped.forEach((item) => {
    const key = item.name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    dedupedByName.push(item);
  });

  return dedupedByName;
};

export const fetchPublicListingsByCategory = async (categoryId) => {
  const listingEndpointTemplate =
    appSettings.endpoints.getPublicListingsByCategory ||
    "/public/listings-by-category/{categoryId}";

  if (!categoryId || categoryId <= 0) return [];

  const endpoint = listingEndpointTemplate.replace("{categoryId}", String(categoryId));
  const response = await fetch(`${appSettings.baseUrl}${endpoint}`);
  if (!response.ok) return [];

  const payload = await response.json();
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return items;
};
